import { useMemo } from "react";

import {
  CaseQueueColumnSchemaEntityRef,
  CaseQueueColumnSchemas,
  EnrichedDeserializedQueueCase,
} from "src/cases/queues/types";
import {
  EntityRef,
  useEntityDisplayValues,
} from "src/entities/useEntityDisplayValues";
import { Environment } from "src/hooks/useEnvironment";

type UseEntityDisplayMapParams = {
  cases: EnrichedDeserializedQueueCase[];
  columnSchemas: CaseQueueColumnSchemas;
  environment: Environment;
  enabled?: boolean;
};

const collectEntityReferences = (
  cases: EnrichedDeserializedQueueCase[],
  columnSchemas: CaseQueueColumnSchemas,
): EntityRef[] => {
  const entityRefColumns = Object.values(columnSchemas).filter(
    (columnSchema): columnSchema is CaseQueueColumnSchemaEntityRef =>
      columnSchema?.type === "entity_ref",
  );

  const refs: EntityRef[] = [];
  const seen = new Set<string>();

  cases.forEach((caseData) => {
    entityRefColumns.forEach((columnSchema) => {
      const propertyKey = columnSchema.__key;
      const entityId = caseData.case_properties[propertyKey];

      if (
        entityId &&
        typeof entityId === "string" &&
        columnSchema.template_version_ids.has(caseData.template_version.id) &&
        !seen.has(entityId)
      ) {
        seen.add(entityId);
        refs.push({ id: entityId, entity_type: columnSchema.entity_type });
      }
    });
  });

  return refs;
};

export const useEntityDisplayMap = ({
  cases,
  columnSchemas,
  environment,
  enabled = true,
}: UseEntityDisplayMapParams) => {
  const entityRefs = useMemo(
    () => (enabled ? collectEntityReferences(cases, columnSchemas) : []),
    [cases, columnSchemas, enabled],
  );

  return useEntityDisplayValues({
    entityRefs,
    environment,
    enabled,
  });
};
